import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;
interface RunCodeEventData {
    function: { params: string[]; args: any[] };
    transactionId: string;
    browser: string;
    browserMajor: number;
}
export interface RunCodeRes {
    type?: never;
    status: 'done' | 'failed';
    success: boolean;
    result: { resultValue: any; exports: object; exportsTest: object; exportsGlobal: object };
}
export interface HangingPromiseRes {
    type: 'promise';
    status?: never;
    success?: never;
    result?: never;
}

export const runCode = function (eventData: RunCodeEventData, preCompiledCode: ((...args: unknown[]) => any) | ((...args: unknown[]) => Promise<any>) | undefined) {
    function appendToStorage(name: string, data: HangingPromiseRes | RunCodeRes) {
        const sessionItem = `data-testim-${name}`;

        const nativeFuncErrMsg = 'Native sessionStorage is not available';
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        function isNativeFunction(fn: Function) {
            if (!fn?.toString) {
                return false;
            }
            return fn.toString().includes('[native code]');
        }
        try {
            if (![window.sessionStorage.setItem, window.sessionStorage.getItem].every(isNativeFunction)) {
                throw new Error(nativeFuncErrMsg);
            }
            const oldData = JSON.parse(window.sessionStorage.getItem(sessionItem) || '{}');
            const newData = { ...oldData, ...data };
            window.sessionStorage.setItem(sessionItem, JSON.stringify(newData));
        } catch (err) {
            // any variation QuotaExceededError from browsers
            const isQuotaExceededError = err.message.toLowerCase().includes('quota');
            const isNativeFunctionError = err.message === nativeFuncErrMsg;

            if (
                err.message.includes('sessionStorage') || // Chrome + Firefox
                err.message.includes('The operation is insecure') || // Safari
                isQuotaExceededError ||
                isNativeFunctionError
            ) {
                let storage = document.head.querySelector('#testim-storage-backup');
                if (!storage) {
                    storage = document.createElement('meta');
                    storage.id = 'testim-storage-backup';
                    document.head.append(storage);
                }
                const oldData = JSON.parse(storage.getAttribute(sessionItem) || '{}');
                const newData = { ...oldData, ...data };
                storage.setAttribute(sessionItem, JSON.stringify(newData));
                if (isQuotaExceededError || isNativeFunctionError) {
                    try {
                        window.sessionStorage.removeItem(sessionItem);
                    } catch (e) {
                        // Prevents future retries from looking in sessionStorage with old data
                    }
                }
                return;
            }
            throw err;
        }
    }

    function getExecutionArguments(_eventData: RunCodeEventData) {
        // New code with stepParamsBuilder
        if (_eventData.function) {
            return _eventData.function.args
                .map((arg) => (arg?.locatedElement ? getLocatedElement(arg.locatedElement) : arg));
        }

        // @ts-expect-error this is Old code, not sure it is relevant anymore
        return _eventData.directParams
            .map((p: any) => (p.selector ? document.querySelector(p.selector) : p.value))
            // @ts-expect-error this is Old code, not sure it is relevant anymore
            .concat(_eventData.otherParams);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    function constructWithArguments(constructor: Function, args: string[]) {
        function F() {
            // eslint-disable-next-line prefer-spread
            return constructor.apply(null, args);
        }

        F.prototype = constructor.prototype;
        // @ts-expect-error dark magic
        return new F();
    }

    const exportedData = {};
    const exportedTestData = {};
    const exportedGlobalData = {};

    // @ts-expect-error this property doesn't exist
    const functionParams = eventData.functionParams;
    const transactionId = eventData.transactionId;

    try {
        const args = getExecutionArguments(eventData);

        args.push(exportedData, exportedTestData, exportedGlobalData);

        const params = eventData.function?.params || functionParams;

        const functionToRun = preCompiledCode || constructWithArguments(Function, params);
        // eslint-disable-next-line prefer-spread
        const result = functionToRun.apply(null, args);

        if (typeof Promise !== 'undefined' && result instanceof Promise) {
            appendToStorage(transactionId, { type: 'promise' });
            result.then((res) => {
                appendToStorage(transactionId, {
                    status: 'done',
                    success: true,
                    result: {
                        resultValue: res,
                        exports: exportedData,
                        exportsTest: exportedTestData,
                        exportsGlobal: exportedGlobalData,
                    },
                });
            }, (res) => {
                appendToStorage(transactionId, {
                    status: 'failed',
                    success: false,
                    result: {
                        resultValue: res.toString(),
                        exports: exportedData,
                        exportsTest: exportedTestData,
                        exportsGlobal: exportedGlobalData,
                    },
                });
            });
        } else {
            appendToStorage(transactionId, {
                status: 'done',
                success: true,
                result: {
                    resultValue: result,
                    exports: exportedData,
                    exportsTest: exportedTestData,
                    exportsGlobal: exportedGlobalData,
                },
            });
        }
    } catch (e) {
        appendToStorage(transactionId, {
            status: 'failed',
            success: false,
            result: {
                resultValue: e.toString(),
                exports: exportedData,
                exportsTest: exportedTestData,
                exportsGlobal: exportedGlobalData,
            },
        });
    }
};
